// eslint-disable @typescript-eslint/no-explicit-any

import { Options as HighChartOptions, PaneOptions, PointOptionsObject, SeriesAreasplineOptions, XAxisPlotBandsOptions, SeriesArearangeOptions } from "highcharts";
import { ColorMap, ComparisonItem, RadarChartProviderOptions, RadarChartTheme } from "./HighChartThemes";
import { TRAITS, TRAIT_GROUPINGS, TRAITS_FOUR_GROUP, FOUR_GROUP_TRAIT_GROUPINGS, TraitFourGroup } from "@/lib/consts";

const TRAIT_BANDS = [
  /* TODO: Name to resource String */
  { color: "#222831", from: 0, to: 1.5, name: "Action-Oriented", traits: TRAIT_GROUPINGS["action-oriented"], angle: "9%" },
  { color: "#393E46", from: 1.5, to: 2.5, name: "Adaptable", traits: TRAIT_GROUPINGS["adaptable"], angle: "17%" },
  { color: "#00ADB5", from: 2.5, to: 4.5, name: "Rigorous", traits: TRAIT_GROUPINGS["dedicated"], angle: "26%" },
  { color: "#525FE1", from: 4.5, to: 6.5, name: "Influential", traits: TRAIT_GROUPINGS["influential"], angle: "40%", mirror: true },
  { color: "#F86F03", from: 6.5, to: 9.5, name: "Forward-Focused", traits: TRAIT_GROUPINGS["insightful"], angle: "57%", mirror: true },
  { color: "#FFA41B", from: 9.5, to: 12.5, name: "People-Oriented", traits: TRAIT_GROUPINGS["people-oriented"], angle: "77%" },
  { color: "#525FE1", from: 12.5, to: 14.5, name: "Principles-Driven", traits: TRAIT_GROUPINGS["principled"], angle: "90%" },
];

const FOUR_TRAIT_BANDS = [
  {
    color: "#222831",
    from: 0,
    to: 3.5,
    name: "Strategic Orientation",
    traits: FOUR_GROUP_TRAIT_GROUPINGS["strategic-orientation"],
    angle: "12%",
  },
  {
    color: "#393E46",
    from: 3.5,
    to: 7,
    name: "People Orientation",
    traits: FOUR_GROUP_TRAIT_GROUPINGS["poeple-orientation"],
    angle: "30%",
    slice: { startAngle: 24 * 11, endAngle: 24 * 15, fillColor: "#6ebcdd57" },
    mirror: true,
  },
  {
    color: "#00ADB5",
    from: 7,
    to: 10.5,
    name: "Outcome Orientation",
    traits: FOUR_GROUP_TRAIT_GROUPINGS["outcome-orientation"],
    angle: "57%",
    mirror: true,
  },
  { color: "#525FE1", from: 10.5, to: 14.5, name: "Communication Orientation", traits: FOUR_GROUP_TRAIT_GROUPINGS["communication-orientation"], angle: "87%", slice: { startAngle: 24 * 3, endAngle: 24 * 6 } },
];

function getTraits(fourGroup?: boolean) {
  if (fourGroup) {
    return TRAITS_FOUR_GROUP;
  }
  return TRAITS;
}

function isTraitWhiteText(trait: string, fourGroup?: boolean) {
  if (fourGroup) {
    return FOUR_TRAIT_BANDS.some((band) => band.traits.includes(trait as TraitFourGroup) && band.slice);
  }
  return false;
  // return TRAIT_BANDS.some((band) => band.traits.includes(trait) && band.slice !== undefined);
}

type ExtendedSeriesAreasplineOptions<T> = SeriesAreasplineOptions & { comparisonItem: T; comparisonObject: ComparisonItem<T> };
type ExtendedSeriesRangeOptions<T> = SeriesArearangeOptions & { comparisonItem: T; comparisonObject: ComparisonItem<T> };
type ExtendedComparisonItem<T> = ComparisonItem<T & { traitScores?: Record<string, number>; competencyScores?: Record<string, number>; traitRanges?: Record<string, number[]> }>;

export const TraitChartOptions = {};

export const TraitChartSeriesProvider =
  (options: RadarChartProviderOptions) =>
  <T>(comparisonItem: ExtendedComparisonItem<T>, getName: (item: T, showNames: boolean) => string): ExtendedSeriesAreasplineOptions<T> => ({
    type: "areaspline",
    comparisonObject: comparisonItem,
    comparisonItem: comparisonItem.data,
    id: comparisonItem.id,
    name: getName(comparisonItem.data, options.showNames),
    data: getTraits(options.fourGroup).map((s) => ({ y: comparisonItem.data.traitScores ? comparisonItem.data.traitScores[s] : 0, comparisonItem, trait: s } as PointOptionsObject)),
    lineColor: comparisonItem.color || ColorMap[options.theme]["default"],
    fillColor: comparisonItem.data.traitRanges ? "transparent" : (comparisonItem.color || ColorMap[options.theme]["default"]) + "26",
    className: `series-${comparisonItem.id}`,
    marker: {
      symbol: "circle",
      enabled: true,
      states: {
        hover: {
          enabled: true,
        },
      },
      lineColor: comparisonItem.color || ColorMap[options.theme]["default"],
      fillColor: comparisonItem.color || ColorMap[options.theme]["default"],
    },
    states: {
      inactive: {
        opacity: 1,
      },
    },
    point: {
      events: {
        // Hovering a point bolds that competency's axis label. Nothing is faded — the
        // other labels and orientation bands keep full opacity (ClickUp 868m3bmkk).
        mouseOver: (function (options) {
          return function (this: any) {
            [...this.series.xAxis.labelGroup.div.getElementsByTagName("span")]
              .filter((e) => e.childNodes[0] && e.childNodes[0].attributes && e.childNodes[0].attributes.getNamedItem("data-trait")?.value === this.trait)
              .forEach((e) => e.classList.add("bold-label"));
            if (options.onMouseOver) {
              options.onMouseOver.bind(this.series)(null);
            }
          };
        })(options),
        mouseOut: (function (options) {
          return function (this: any) {
            [...this.series.xAxis.labelGroup.div.getElementsByTagName("span")].forEach((e) => e.classList.remove("bold-label"));

            if (options.onMouseOut) {
              options.onMouseOut.bind(this.series)(null);
            }
          };
        })(options),
      },
    },
  });

export const TraitChartBandSeriesProvider =
  (options: RadarChartProviderOptions) =>
  <T>(comparisonItem: ExtendedComparisonItem<T>, getName: (item: T, showNames: boolean) => string): ExtendedSeriesRangeOptions<T> => {
    const rangeProviderItem = {
      type: "areasplinerange",
      // Own id so chart.update() doesn't match this band onto the benchmark series by index
      // (which would apply the band's 0.15 opacity to the benchmark line instead of adding a band).
      id: `${comparisonItem.id}-band`,
      comparisonObject: comparisonItem,
      comparisonItem: comparisonItem.data,
      name: getName(comparisonItem.data, options.showNames),
      data: getTraits(options.fourGroup).map((s) => (comparisonItem.data.traitRanges ? comparisonItem.data.traitRanges[s] : [0, 0])),
      animation: false,
      point: {},
      lineColor: "transparent",
      linkedTo: comparisonItem.id,
      opacity: 0.15,
      // Highcharts would otherwise dim the band to 0.2 while another series is hovered.
      states: { inactive: { opacity: 0.15 } },
      enableMouseTracking: false,
      marker: { enabled: false },
      fillColor: comparisonItem.color || ColorMap[options.theme]["default"],
    };

    return rangeProviderItem as ExtendedSeriesRangeOptions<T>;
  };

export const TraitChartOptionsProvider = (options: RadarChartProviderOptions): Partial<HighChartOptions> => ({
  chart: {
    ...RadarChartTheme.chart,
    events: {
      render: function (this: Highcharts.Chart & { pane?: PaneOptions[] }) {
        const svg = this.renderer;
        const tratTextNodes = this.container.querySelectorAll("text[data-trait]");

        tratTextNodes.forEach((e) => e.remove());
        svg.defs.element.querySelectorAll("[id='PlotBandsCurvedPath']").forEach((e) => e.remove());
        // Remove existing background elements if they exist
        this.container.querySelectorAll(".chart-background-circle").forEach((e) => e.remove());
        this.container.querySelectorAll(".chart-donut-ring").forEach((e) => e.remove());
        this.container.querySelectorAll(".chart-donut-slice").forEach((e) => e.remove());
        this.container.querySelectorAll(".chart-pizza-slice").forEach((e) => e.remove());

        if (this.pane && this.pane[0] && this.pane[0].center) {
          const cx = this.plotLeft + this.plotWidth / 2;
          const cy = this.plotTop + this.plotHeight / 2;
          const r = parseInt(this.pane[0].center[2].toString(), 10) / 2 + 5 + 120; // Added 75px gap
          const bgBandWidth = 5;
          const backgroundCircleRadius = parseInt(this.pane[0].center[2].toString(), 10) / 2; // Size of chart
          const backgroundCircleColour = "#F8F9FC";
          // Shaded orientation slices (marketing's lighter suggestion, ClickUp 868kwkuw2).
          const backgroundAltColour = "#D3E5EE";

          // Create background circle behind the polar chart
          svg
            .circle(cx, cy, backgroundCircleRadius)
            .attr({
              fill: backgroundCircleColour,
              stroke: "none",
              zIndex: 0, // Place behind everything else
              class: "chart-background-circle",
            })
            .add();

          // Create donut ring around the polar chart using two circles
          const donutOuterRadius = backgroundCircleRadius + 10 + 100; // 10px gap + 75px height
          const donutInnerRadius = backgroundCircleRadius + 10; // 10px gap from chart edge

          // Create outer circle for donut
          svg
            .circle(cx, cy, donutOuterRadius)
            .attr({
              fill: backgroundCircleColour,
              stroke: "none",
              class: "chart-donut-ring chart-donut-outer",
            })
            .add();

          // Create inner circle to create the donut hole (same color as chart background)
          svg
            .circle(cx, cy, donutInnerRadius)
            .attr({
              fill: "#FFFFFF", // White to create the hole effect
              stroke: "none",
              class: "chart-donut-ring chart-donut-inner",
            })
            .add();

          // Create slices based on FOUR_TRAIT_BANDS configuration
          const centerX = parseFloat(cx.toString());
          const centerY = parseFloat(cy.toString());
          const radius = parseFloat(backgroundCircleRadius.toString());

          if (centerX && centerY && radius && options.fourGroup) {
            // Helper function to convert angle to radians
            const toRadians = (angle: number) => (angle * Math.PI) / 180;

            // Helper function to get point on circle
            const getPoint = (centerX: number, centerY: number, radius: number, angle: number) => ({
              x: centerX + radius * Math.cos(toRadians(angle - 90)), // -90 to start from top
              y: centerY + radius * Math.sin(toRadians(angle - 90)),
            });

            FOUR_TRAIT_BANDS.forEach((band, index) => {
              if (band.slice) {
                const { startAngle, endAngle } = band.slice;

                // Calculate points for pizza slice
                const startPoint = getPoint(centerX, centerY, radius, startAngle);
                const endPoint = getPoint(centerX, centerY, radius, endAngle);
                const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

                // Create pizza slice path
                const pizzaSlicePath = `M ${centerX} ${centerY} L ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endPoint.x} ${endPoint.y} Z`;

                const pizzaSlice = svg.createElement("path");
                pizzaSlice.attr({
                  d: pizzaSlicePath,
                  fill: backgroundAltColour,
                  stroke: "none",
                  class: `chart-pizza-slice chart-slice-${index}`,
                  zIndex: 0,
                });
                pizzaSlice.add();

                // Calculate points for donut slice
                const donutStartOuter = getPoint(centerX, centerY, donutOuterRadius, startAngle);
                const donutEndOuter = getPoint(centerX, centerY, donutOuterRadius, endAngle);
                const donutStartInner = getPoint(centerX, centerY, donutInnerRadius, startAngle);
                const donutEndInner = getPoint(centerX, centerY, donutInnerRadius, endAngle);

                // Create donut slice path
                const donutSlicePath = `M ${donutStartOuter.x} ${donutStartOuter.y} A ${donutOuterRadius} ${donutOuterRadius} 0 ${largeArcFlag} 1 ${donutEndOuter.x} ${donutEndOuter.y} L ${donutEndInner.x} ${donutEndInner.y} A ${donutInnerRadius} ${donutInnerRadius} 0 ${largeArcFlag} 0 ${donutStartInner.x} ${donutStartInner.y} Z`;

                const donutSlice = svg.createElement("path");
                donutSlice.attr({
                  d: donutSlicePath,
                  fill: backgroundAltColour,
                  stroke: "none",
                  class: `chart-donut-slice chart-slice-${index}`,
                  zIndex: 0,
                });
                donutSlice.add();
              }
            });
          }

          svg
            .path()
            .attr({
              id: "PlotBandsCurvedPath",
              d: `M ${cx} ${cy} m 0 ${-r} a ${r} ${r} 0 1 1 0 ${r * 2} a ${r} ${r} 1 1 1 0 ${-(r * 2)}`,
            })
            .add(svg.defs);

          svg
            .path()
            .attr({
              id: "PlotBandsCurvedPathBG",
              d: `M ${cx} ${cy} m 0 ${-r - bgBandWidth} a ${r} ${r} 0 1 1 0 ${(r + bgBandWidth) * 2} a ${r} ${r} 1 1 1 0 ${-((r + bgBandWidth) * 2)}`,
            })
            .add(svg.defs);

          if (this.axes.length > 0) {
            const bands = (this.axes[0] as any).plotLinesAndBands as XAxisPlotBandsOptions[];

            // svg
            //   .createElement("use")
            //   .attr({
            //     href: "#PlotBandsCurvedPathBG",
            //     stroke: "#F8F9FC",
            //     "stroke-width": 25,
            //     "clip-path": "url(#clip)",
            //   })
            //   .add();

            for (const bandInx in bands) {
              let trait = TRAIT_BANDS[bandInx];
              if (options.fourGroup) {
                trait = FOUR_TRAIT_BANDS[bandInx];
              }

              const label = svg
                .text()
                .attr({
                  zIndex: 3, // place on top of a pie
                  "text-anchor": "middle", // center text in a slice (middle angle)
                  "data-trait": trait.name,
                  class: `PlotBandsCurvedPath band-${bandInx}`,
                })
                .add();

              let offset = TRAIT_BANDS[bandInx].angle; // ((100 * (45 + Math.PI / 2)) / (Math.PI * 2)).toString() + "%";
              if (options.fourGroup) {
                offset = FOUR_TRAIT_BANDS[bandInx].angle;
              }
              const textPath = document.createElementNS("http://www.w3.org/2000/svg", "textPath");
              const tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");

              if (trait.mirror) {
                tspan.setAttribute("rotate", "180");
                tspan.setAttribute("dy", "10");
                tspan.appendChild(document.createTextNode(trait.name.split("").reverse().join("")));
              } else {
                tspan.appendChild(document.createTextNode(trait.name));
              }

              textPath.setAttributeNS("http://www.w3.org/1999/xlink", "href", "#PlotBandsCurvedPath");
              textPath.setAttribute("startOffset", offset);
              textPath.setAttribute("transform", "rotate(90,100,100)");

              textPath.appendChild(tspan);
              label.element.appendChild(textPath);
            }
          }
        }
      },
    },
  },
  plotOptions: {
    series: {
      pointPlacement: "on", // align the data points with the tick marks
    },
  },
  tooltip: {
    enabled: false,
  },
  xAxis: {
    labels: {
      allowOverlap: true,
      style: {
        whiteSpace: "normal", // set to normal
      },
      useHTML: true,
      distance: 60,
      align: "center",
    },
    // Radial spokes; the concentric rings are RadarChartTheme.yAxis.gridLineColor.
    gridLineColor: "#CCD0D7",
    tickInterval: 1,
    categories: getTraits(options.fourGroup).map((trait) =>
      options.resourceLookup
        ? `<span class="x-axis-label ${isTraitWhiteText(trait, options.fourGroup) ? "white-text" : ""} data-trait="` +
            trait +
            '" title="' +
            options.resourceLookup(`Report::NowWhat::Competency::Description::${trait}`) +
            '">' +
            options.resourceLookup(`Comparison::TraitChart::XAxisLabel::${trait}`)?.toString() +
            ' <i class="bi bi-info-circle-fill comparison-axis-hover"></i></span>' ?? trait
        : trait,
    ) /* TODO: Lookup text from literals */,
    plotBands: [
      ...(options.fourGroup ? FOUR_TRAIT_BANDS : TRAIT_BANDS).map(
        (t) =>
          ({
            from: t.from,
            to: t.to,
            color: "#FFFFFF",
            traits: t.traits,
            borderWidth: 10,
            label: {
              text: t.name,
              style: {
                display: "none",
                color: t.color,
                fontWeight: "bold",
                fontSize: "18px",
              },
            },
          } as XAxisPlotBandsOptions & { traits: string[] }),
      ),
    ],
  },
});

export const TraitChartCompactOptionsProvider = (options: RadarChartProviderOptions): Partial<HighChartOptions> => ({
  chart: {
    ...RadarChartTheme.chart,
    height: 360,
    marginTop: 16,
    marginBottom: 16,
    backgroundColor: "transparent",
    events: {
      render: function (this: Highcharts.Chart & { pane?: PaneOptions[] }) {
        const svg = this.renderer;

        // Idempotent cleanup so re-renders (resize, hot reload) don't stack labels or wedges.
        this.container.querySelectorAll("text[data-trait]").forEach((e) => e.remove());
        this.container.querySelectorAll(".chart-pizza-slice-compact").forEach((e) => e.remove());
        svg.defs.element.querySelectorAll("[id='PlotBandsCurvedPathCompact']").forEach((e) => e.remove());

        if (this.pane && this.pane[0] && this.pane[0].center) {
          const cx = this.plotLeft + this.plotWidth / 2;
          const cy = this.plotTop + this.plotHeight / 2;
          // Matches the existing TraitChartOptionsProvider pattern — at render time
          // pane[0].center[2] is the resolved pixel radius even though its config type is a percentage string.
          const paneRadius = parseInt(this.pane[0].center[2].toString(), 10) / 2;
          const labelRadius = paneRadius + 14;

          // Background pizza wedges differentiating People + Communication orientations
          // (FOUR_TRAIT_BANDS entries with a `slice` config). Drawn behind the polygon at zIndex 0.
          if (options.fourGroup) {
            const toRadians = (angle: number) => (angle * Math.PI) / 180;
            const getPoint = (centerX: number, centerY: number, r: number, angle: number) => ({
              x: centerX + r * Math.cos(toRadians(angle - 90)),
              y: centerY + r * Math.sin(toRadians(angle - 90)),
            });
            // -12° offset shifts each wedge from "starts at the first group trait" to
            // "starts at the midpoint between this group and the previous one", giving
            // a tight visual match between the wedge edges and the orientation sectors.
            const WEDGE_ANGLE_OFFSET = -12;
            FOUR_TRAIT_BANDS.forEach((band, index) => {
              if (band.slice) {
                const startAngle = band.slice.startAngle + WEDGE_ANGLE_OFFSET;
                const endAngle = band.slice.endAngle + WEDGE_ANGLE_OFFSET;
                const startPt = getPoint(cx, cy, paneRadius, startAngle);
                const endPt = getPoint(cx, cy, paneRadius, endAngle);
                const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
                const slicePath = `M ${cx} ${cy} L ${startPt.x} ${startPt.y} A ${paneRadius} ${paneRadius} 0 ${largeArcFlag} 1 ${endPt.x} ${endPt.y} Z`;

                const pizzaSlice = svg.createElement("path");
                pizzaSlice.attr({
                  d: slicePath,
                  fill: "rgba(118,188,218,0.18)",
                  stroke: "none",
                  class: `chart-pizza-slice-compact chart-slice-${index}`,
                  zIndex: 0,
                });
                pizzaSlice.add();
              }
            });
          }

          svg
            .path()
            .attr({
              id: "PlotBandsCurvedPathCompact",
              d: `M ${cx} ${cy} m 0 ${-labelRadius} a ${labelRadius} ${labelRadius} 0 1 1 0 ${labelRadius * 2} a ${labelRadius} ${labelRadius} 1 1 1 0 ${-(labelRadius * 2)}`,
            })
            .add(svg.defs);

          if (this.axes.length > 0) {
            const bands = (this.axes[0] as any).plotLinesAndBands as XAxisPlotBandsOptions[];

            for (let bandInx = 0; bandInx < bands.length; bandInx++) {
              const trait = options.fourGroup ? FOUR_TRAIT_BANDS[bandInx] : TRAIT_BANDS[bandInx];
              if (!trait) continue;

              const label = svg
                .text()
                .attr({
                  zIndex: 3,
                  "text-anchor": "middle",
                  "data-trait": trait.name,
                  class: `PlotBandsCurvedPath band-${bandInx} compact-orientation-label`,
                  fill: "rgba(180,210,228,0.9)",
                  "font-size": "14px",
                  "font-weight": "600",
                })
                .add();

              // text-anchor="middle" (set on the parent <text>) makes startOffset the visual center
              // of the label, with these offsets tuned so each label sits over its sector:
              //   Strategic 7.5% — slightly past 6.67% midpoint to keep the leading "S" from clipping.
              //   People    26.67% — exact sector midpoint (96°/360°).
              //   Outcome   49% — slight CCW shift from 50% so the asymmetric space in
              //                  the mirrored "Outcome Orientation" lands at 6 o'clock.
              //   Communication 84% — aligned with the visual midpoint of the wedge (~304°)
              //                       rather than the sector midpoint (288°), since the wedge
              //                       is narrower than the sector.
              const FOUR_GROUP_LABEL_CENTERS = ["7.5%", "26.67%", "53%", "84%"];
              const offset = options.fourGroup ? FOUR_GROUP_LABEL_CENTERS[bandInx] : trait.angle;
              const textPath = document.createElementNS("http://www.w3.org/2000/svg", "textPath");
              const tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");

              if (trait.mirror) {
                tspan.setAttribute("rotate", "180");
                tspan.setAttribute("dy", "10");
                tspan.appendChild(document.createTextNode(trait.name.split("").reverse().join("")));
              } else {
                tspan.appendChild(document.createTextNode(trait.name));
              }

              textPath.setAttributeNS("http://www.w3.org/1999/xlink", "href", "#PlotBandsCurvedPathCompact");
              textPath.setAttribute("startOffset", offset);
              textPath.setAttribute("transform", "rotate(90,100,100)");

              textPath.appendChild(tspan);
              label.element.appendChild(textPath);
            }
          }
        }
      },
    },
  },
  pane: {
    startAngle: 0,
    endAngle: 360,
    center: ["50%", "50%", "95%"],
  },
  plotOptions: {
    series: {
      pointPlacement: "on",
      enableMouseTracking: true,
      states: { inactive: { opacity: 1 } },
    },
  },
  tooltip: {
    enabled: true,
    useHTML: true,
    backgroundColor: "rgba(11,32,60,0.95)",
    borderColor: "rgba(255,255,255,0.2)",
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
    style: { color: "#ffffff", fontSize: "12px" },
    shadow: false,
    formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      const opts = this.point.options as unknown as { trait?: string; priority?: "high" | "mixed" | "low" };
      const traitKey = opts.trait ?? "";
      const traitName = options.resourceLookup ? options.resourceLookup(`Comparison::TraitChart::XAxisLabel::${traitKey}`) : traitKey;
      const p = opts.priority;
      const chip =
        p === "high"
          ? `<span style="color:#9bcc50">● Highly Prioritized</span>`
          : p === "mixed"
          ? `<span style="color:#f0c419">● Prioritized</span>`
          : p === "low"
          ? `<span style="color:#8b93ab">● Less Prioritized</span>`
          : "";
      return `<div><strong>${traitName}</strong>${chip ? `<br/>${chip}` : ""}</div>`;
    },
  },
  xAxis: {
    tickInterval: 1,
    categories: [...getTraits(options.fourGroup)],
    labels: { enabled: false },
    lineColor: "rgba(255,255,255,0.15)",
    gridLineColor: "rgba(255,255,255,0.15)",
    tickColor: "transparent",
    plotBands: (options.fourGroup ? FOUR_TRAIT_BANDS : TRAIT_BANDS).map(
      (t) =>
        ({
          from: t.from,
          to: t.to,
          color: "transparent",
          borderWidth: 0,
        } as XAxisPlotBandsOptions),
    ),
  },
  yAxis: {
    min: 0,
    max: 110,
    tickInterval: 25,
    labels: { enabled: false },
    gridLineColor: "rgba(255,255,255,0.15)",
    gridLineWidth: 1,
  },
});
